import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useApiClient } from '@/api/client'
import { useWorkspace } from '@/workspaces/WorkspaceContext'
import { usePeople } from '@/api/people/usePeople'

export type ProjectState = 'on_track' | 'at_risk' | 'delayed' | 'on_hold' | 'completed'

export type EnrichedStatus = {
  slug: string
  label: string
  color: string
  category: string
  order: number
}

export type ProjectFlat = {
  id: string
  workspace_id: string
  object_type: 'project'
  created_at: string
  updated_at: string
  created_by?: string
  archived_at?: string
  name: string
  description?: string
  short_summary?: string
  last_health_update?: Record<string, unknown>
  status?: EnrichedStatus | null
  state?: ProjectState
  owner?: string | null
  start_date?: string
  end_date?: string
  /**
   * reference_list к person — кто имеет доступ к проекту. На бэкенде
   * flattenRecord поднимает все атрибуты records в корень JSON-ответа,
   * поэтому members приходит на верхнем уровне, а не в `attributes`.
   */
  members?: string[] | null
  attributes?: Record<string, unknown>
}

export type ProjectRecord = {
  id: string
  name: string
  description: string
  attributes: Record<string, unknown>
  state: ProjectState
  stateLabel: string
  status: EnrichedStatus | null
  ownerId: string | null
  ownerName: string
  ownerAvatarUrl: string
  startDate: string
  endDate: string
  createdAt: string
  updatedAt: string
}

const STATE_LABELS: Record<ProjectState, string> = {
  on_track: 'По плану',
  at_risk: 'Риск',
  delayed: 'Задержка',
  on_hold: 'На паузе',
  completed: 'Завершён',
}

export function useProjects() {
  const { fetchWithAuth } = useApiClient()
  const { workspace } = useWorkspace()
  const slug = workspace.slug
  const { peopleById } = usePeople()

  const query = useQuery({
    queryKey: ['projects', slug],
    queryFn: () =>
      fetchWithAuth<{ items: ProjectFlat[] }>(`/api/v1/workspaces/${slug}/projects`).then(
        (r) => r.items,
      ),
    enabled: Boolean(slug),
    staleTime: 30_000,
  })

  const projects = useMemo<ProjectRecord[]>(() => {
    const raw = query.data ?? []
    return raw.map((flat) => {
      const state = flat.state ?? 'on_track'
      const owner = flat.owner ? peopleById.get(flat.owner) : null
      return {
        id: flat.id,
        name: flat.name,
        description: flat.description ?? '',
        attributes: {
          ...(flat.attributes ?? {}),
          name: flat.name,
          description: flat.description ?? '',
          short_summary: flat.short_summary ?? '',
          last_health_update: flat.last_health_update ?? null,
          owner: flat.owner ?? null,
          start_date: flat.start_date ?? '',
          end_date: flat.end_date ?? '',
          members: flat.members ?? null,
          created_at: flat.created_at,
          updated_at: flat.updated_at,
          state,
        },
        state,
        stateLabel: STATE_LABELS[state] ?? state,
        status: flat.status ?? null,
        ownerId: flat.owner ?? null,
        ownerName: owner?.name ?? '',
        ownerAvatarUrl: owner?.avatarUrl ?? '',
        startDate: flat.start_date ?? '',
        endDate: flat.end_date ?? '',
        createdAt: flat.created_at,
        updatedAt: flat.updated_at,
      }
    })
  }, [query.data, peopleById])

  return {
    projects,
    raw: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
